import React, { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from 'react-oidc-context';
import './App.css';

function App() {
  const auth = useAuth();

  const [packerType, setPackerType] = useState('vmp3');
  const [mainFile, setMainFile] = useState(null);
  const [stubFile, setStubFile] = useState(null);
  const [packmanOptions, setPackmanOptions] = useState({
    removeStubSection: false, removeCertTable: false, patchCallbacks: false,
    disableVanguard: false, disableAslr: false,
  });
  const [status, setStatus] = useState('idle');
  const [logs, setLogs] = useState([]);
  const consoleEndRef = useRef(null);
  const [downloadUrl, setDownloadUrl] = useState(null);

  useEffect(() => {
    if (auth.isLoading || auth.isAuthenticated || auth.activeNavigator) return;
    if (auth.error) {
      console.error("Authentication Error:", auth.error);
      return;
    }
    auth.signinRedirect();
  }, [auth.isLoading, auth.isAuthenticated, auth.activeNavigator, auth.error, auth.signinRedirect]);

  useEffect(() => {
    consoleEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  const addLog = (level, message) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, { level, message: `[${timestamp}] ${message}` }]);
  };

  const handleDragOver = useCallback((e) => e.preventDefault(), []);

  const handleDrop = useCallback((e, fileSetter) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      fileSetter(file);
      addLog('info', `[INPUT] ${file.name} loaded.`);
    }
  }, []);

  const handleFileChange = (e, fileSetter) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      fileSetter(file);
      addLog('info', `[INPUT] ${file.name} loaded.`);
      e.target.value = null;
    }
  };

  const handleOptionChange = (e) => {
    const { name, checked } = e.target;
    setPackmanOptions(prev => ({ ...prev, [name]: checked }));
  };

  const handleSubmit = async () => {
    if (!mainFile) {
      addLog('error', "[SYSTEM] Unpacking target file is not specified.");
      return;
    }
    if (packerType === 'packman' && !stubFile) {
      addLog('error', "[SYSTEM] Packman requires a stub.dll file.");
      return;
    }

    setLogs([]);
    addLog('info', `[SYSTEM] Starting unpack process for '${mainFile.name}' with ${packerType}...`);
    setStatus('unpacking');
    setDownloadUrl(null);

    const CHUNK_SIZE = 95 * 1024 * 1024;
    const totalChunks = Math.ceil(mainFile.size / CHUNK_SIZE);
    const uploadId = crypto.randomUUID();

    try {
      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
        const start = chunkIndex * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, mainFile.size);
        const chunk = mainFile.slice(start, end);

        const chunkFormData = new FormData();
        chunkFormData.append('chunk', chunk);
        chunkFormData.append('uploadId', uploadId);
        chunkFormData.append('chunkIndex', chunkIndex.toString());

        addLog('info', `[SYSTEM] Uploading chunk ${chunkIndex + 1} of ${totalChunks}...`);

        await axios.post('https://gateway.unpacker.tools.sg1.labs.aslr.ac/v1/upload-chunk', chunkFormData, {
          headers: { 'Authorization': `Bearer ${auth.user?.access_token}` }
        });
      }

      addLog('info', `[SYSTEM] All chunks uploaded. Merging file...`);

      const completeFormData = new FormData();
      completeFormData.append('uploadId', uploadId);
      completeFormData.append('fileName', mainFile.name);
      completeFormData.append('totalChunks', totalChunks.toString());
      completeFormData.append('packerType', packerType);
      if (stubFile) completeFormData.append('stubFile', stubFile);
      Object.entries(packmanOptions).forEach(([key, value]) => {
        if (packerType === 'packman') {
          completeFormData.append(key, value.toString());
        }
      });

      const completeResponse = await axios.post('https://gateway.unpacker.tools.sg1.labs.aslr.ac/v1/upload-complete', completeFormData, {
        headers: { 'Authorization': `Bearer ${auth.user?.access_token}` }
      });

      const { jobId } = completeResponse.data;
      addLog('info', `[SYSTEM] Job created with ID: ${jobId}.`);

      let isCompleted = false;
      const eventSource = new EventSource(`https://gateway.unpacker.tools.sg1.labs.aslr.ac/v1/stream/${jobId}?access_token=${auth.user?.access_token}`);

      eventSource.addEventListener('log_info', (event) => addLog('info', event.data));
      eventSource.addEventListener('log_warn', (event) => addLog('warn', event.data));
      eventSource.addEventListener('log_error', (event) => addLog('error', event.data));
      eventSource.addEventListener('complete', (event) => {
        isCompleted = true;
        const completedJobId = event.data;
        addLog('success', `[SYSTEM] Job ${completedJobId} completed successfully.`);
        setDownloadUrl(`/v1/download/${completedJobId}`);
        setStatus('success');
        eventSource.close();
      });
      eventSource.addEventListener('error', (event) => {
        if (isCompleted) return;
        addLog('error', `[FATAL] A stream error occurred or connection was lost.`);
        setStatus('error');
        eventSource.close();
      });

    } catch (err) {
      const message = err.response?.data?.title || err.message;
      addLog('error', `[FATAL] An error occurred: ${message}`);
      setStatus('error');
    }
  };

  const isUnpackButtonDisabled = status === 'unpacking';

  return (
    <div className="app-container">
      <header className="app-header">
        <div className="logo-title">
          <img src="/assets/logo.png" alt="logo" className="logo-image" />
          <h1>Universal Unpacker - v1.0.0</h1>
        </div>
        {auth.isAuthenticated && (
          <div className="user-info">
            <span>Welcome, {auth.user?.profile.name || auth.user?.profile.preferred_username || 'User'}!</span>
            <button onClick={() => auth.signoutRedirect()} className="logout-button">Logout</button>
          </div>
        )}
      </header>
      <div className="app-body">
        <aside className="sidebar">
          <div className="sidebar-section">
            <h2 className="sidebar-title">Packer</h2>
            <div className="packer-selector">
              <button onClick={() => setPackerType('vmp3')} className={packerType === 'vmp3' ? 'active' : ''}>VMProtect 3</button>
              <button onClick={() => setPackerType('themida')} className={packerType === 'themida' ? 'active' : ''}>Themida</button>
              <button onClick={() => setPackerType('packman')} className={packerType === 'packman' ? 'active' : ''}>Packman</button>
            </div>
          </div>
          <div className={`sidebar-section options-section ${packerType === 'packman' ? 'visible' : ''}`}>
            <h2 className="sidebar-title">Packman Options</h2>
            <div className="options-grid">
              <label><input type="checkbox" name="removeStubSection" onChange={handleOptionChange} /> Remove Stub Section</label>
              <label><input type="checkbox" name="removeCertTable" onChange={handleOptionChange} /> Remove Cert Table</label>
              <label><input type="checkbox" name="patchCallbacks" onChange={handleOptionChange} /> Patch Callbacks</label>
              <label><input type="checkbox" name="disableVanguard" onChange={handleOptionChange} /> Disable Vanguard</label>
              <label><input type="checkbox" name="disableAslr" onChange={handleOptionChange} /> Disable ASLR</label>
            </div>
          </div>
          <div className="sidebar-footer">
            <button className="unpack-button" onClick={handleSubmit} disabled={isUnpackButtonDisabled}>
              {status === 'unpacking' ? 'Processing...' : 'Unpack'}
            </button>
            {status === 'success' && downloadUrl && (
              <a
                href={`https://gateway.unpacker.tools.sg1.labs.aslr.ac${downloadUrl}?access_token=${auth.user?.access_token}`}
                className="download-button"
                onClick={() => setTimeout(() => setDownloadUrl(null), 100)}
              >
                Download
              </a>
            )}
          </div>
        </aside>
        <main className="main-content">
          <div className="file-area">
            <div className="file-panel" onDragOver={handleDragOver} onDrop={(e) => handleDrop(e, setMainFile)}>
              <input type="file" id="mainFileInput" className="file-input" onChange={(e) => handleFileChange(e, setMainFile)} />
              <label htmlFor="mainFileInput" className="file-label">
                <span className="file-title">Target File</span>
                <span className="file-name">{mainFile ? mainFile.name : 'Drag & Drop or Click to Upload'}</span>
              </label>
            </div>
            <div className={`file-panel ${packerType !== 'packman' ? 'disabled' : ''}`} onDragOver={handleDragOver} onDrop={(e) => handleDrop(e, setStubFile)}>
              <input type="file" id="stubFileInput" className="file-input" onChange={(e) => handleFileChange(e, setStubFile)} disabled={packerType !== 'packman'} />
              <label htmlFor="stubFileInput" className="file-label">
                <span className="file-title">Stub DLL (Packman)</span>
                <span className="file-name">{stubFile ? stubFile.name : 'Drag & Drop or Click to Upload'}</span>
              </label>
            </div>
          </div>
          <div className="console-area">
            <div className="console-header">Output</div>
            <div className="console-body">
              {logs.map((log, index) => (
                <p key={index} className={`log-${log.level}`}>{log.message}</p>
              ))}
              <div ref={consoleEndRef} />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default App;